import type { LogStreamSource } from "../../api/client";

export interface BufferedLine {
  id: number;
  stream: LogStreamSource;
  message: string;
}

/**
 * Appends `incoming` onto `prev`, then trims from the front so the result
 * never holds more than `max` entries — the client-side ring buffer bound
 * (charter P-6): a chatty container can't grow this list's memory/DOM
 * unbounded, the oldest lines are dropped as new ones arrive.
 *
 * Pure and allocation-only (no mutation of either input) — safe to call
 * directly from a React state updater, including under StrictMode's
 * double-invoke, since the same inputs always produce the same output
 * (FE-M4: no id minting or other side effect happens in here — see
 * LiveLogPanel, which mints ids before this is ever called).
 */
export function appendBounded<T>(prev: readonly T[], incoming: readonly T[], max: number): T[] {
  if (incoming.length === 0) return prev as T[];
  const combined = prev.length === 0 ? incoming.slice() : prev.concat(incoming);
  return combined.length > max ? combined.slice(combined.length - max) : combined;
}
